import React, { useEffect, useMemo, useState } from "react";
import { Search as SearchIcon, RotateCcw, Save, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SITE_URL = "https://accommodationsuppliers.com";
const GOOD = { title: 60, description: 155 };

const Count = ({ value, limit, good, testId }) => {
  const n = (value || "").length;
  const tone = n === 0 ? "text-[#9AA6A0]" : n > limit ? "text-[#B35C4A]" : n > good ? "text-[#8A6508]" : "text-[#1F6B45]";
  return <span className={`tabular-nums text-[11px] ${tone}`} data-testid={testId}>{n}/{good}{n > good && n <= limit ? " · may be cut off" : ""}{n > limit ? " · too long" : ""}</span>;
};

// How the page looks as a Google result.
const GooglePreview = ({ title, description, path, testId }) => {
  const t = title.length > 62 ? `${title.slice(0, 60).replace(/\s+\S*$/, "")}…` : title;
  const d = description.length > 158 ? `${description.slice(0, 155).replace(/\s+\S*$/, "")}…` : description;
  return (
    <div className="bg-white border border-[#E8ECE9] p-4 font-sans max-w-[600px]" data-testid={testId}>
      <div className="flex items-center gap-2 text-[12px] text-[#202124]">
        <span className="w-6 h-6 rounded-full bg-[#F0EFEA] inline-flex items-center justify-center text-[10px] font-semibold text-[#264839]">AS</span>
        <div><div className="leading-tight">Accommodation Suppliers</div><div className="text-[#4d5156] leading-tight">{SITE_URL}{path === "/" ? "" : path}</div></div>
      </div>
      <div className="text-[20px] leading-[1.3] text-[#1a0dab] mt-1 hover:underline cursor-default" data-testid={`${testId}-title`}>{t}</div>
      <div className="text-[14px] leading-[1.58] text-[#4d5156] mt-0.5" data-testid={`${testId}-description`}>{d}</div>
    </div>
  );
};

function SeoRow({ row, suffix, limits, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: row.override?.title || "", description: row.override?.description || "", heading: row.override?.heading || "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm({ title: row.override?.title || "", description: row.override?.description || "", heading: row.override?.heading || "" }); }, [row]);

  const effTitle = (form.title.trim() || row.default.title);
  const fullTitle = /accommodation suppliers/i.test(effTitle) ? effTitle : `${effTitle}${suffix}`;
  const effDesc = form.description.trim() || row.default.description;
  const effHeading = form.heading.trim() || row.default.heading;
  const dirty = form.title !== (row.override?.title || "") || form.description !== (row.override?.description || "") || form.heading !== (row.override?.heading || "");
  const slug = row.key.replace(/[^a-z0-9]+/gi, "-");

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/seo/pages/${encodeURIComponent(row.key)}`, form);
      toast.success(`Saved — ${row.label}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm(`Reset ${row.label} to the generated default title and description?`)) return;
    setBusy(true);
    try { await api.delete(`/admin/seo/pages/${encodeURIComponent(row.key)}`); toast.success("Reset to default"); onSaved(); }
    catch { toast.error("Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`border-t border-[#E8ECE9] ${row.kind === "subcategory" ? "bg-white" : "bg-[#FAFAF7]"}`} data-testid={`seo-row-${slug}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#F0EFEA]/60" data-testid={`seo-row-toggle-${slug}`}>
        <span className={`${row.kind === "subcategory" ? "pl-5 text-sm text-[#4A5550]" : "text-sm font-medium text-[#11211A]"} flex-1 min-w-0`}>
          {row.label}
          <span className="block text-[11px] text-[#9AA6A0] truncate">{row.effective.full_title}</span>
        </span>
        {row.coming_soon && <span className="text-[9px] uppercase tracking-wider text-[#7D8C85]">coming soon</span>}
        {row.customised ? <span className="text-[9px] uppercase tracking-wider font-semibold text-[#1F6B45] bg-[#E6F4EC] px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`seo-customised-${slug}`}><Pencil className="w-2.5 h-2.5" /> Edited</span>
          : <span className="text-[9px] uppercase tracking-wider text-[#9AA6A0]">Default</span>}
        {open ? <ChevronUp className="w-4 h-4 text-[#7D8C85]" /> : <ChevronDown className="w-4 h-4 text-[#7D8C85]" />}
      </button>
      {open && (
        <div className="px-4 pb-5 grid lg:grid-cols-2 gap-5" data-testid={`seo-editor-${slug}`}>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between"><label className="text-[10px] uppercase tracking-wider text-[#7D8C85]">Search title</label><Count value={fullTitle} limit={limits.title} good={GOOD.title} testId={`seo-title-count-${slug}`} /></div>
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={row.default.title} maxLength={limits.title} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] bg-white rounded-sm" data-testid={`seo-title-${slug}`} />
              <div className="text-[10px] text-[#9AA6A0] mt-0.5">Shown as “{fullTitle}”. Leave empty to use the generated default.</div>
            </div>
            <div>
              <div className="flex items-center justify-between"><label className="text-[10px] uppercase tracking-wider text-[#7D8C85]">Search description</label><Count value={effDesc} limit={limits.description} good={GOOD.description} testId={`seo-description-count-${slug}`} /></div>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={row.default.description} maxLength={limits.description} rows={3} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] bg-white rounded-sm resize-y" data-testid={`seo-description-${slug}`} />
            </div>
            <div>
              <div className="flex items-center justify-between"><label className="text-[10px] uppercase tracking-wider text-[#7D8C85]">Main heading on the page (H1)</label><span className="tabular-nums text-[11px] text-[#9AA6A0]">{effHeading.length}</span></div>
              <input value={form.heading} onChange={(e) => setForm((f) => ({ ...f, heading: e.target.value }))} placeholder={row.default.heading || "Uses the page's own headline"} maxLength={limits.heading} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] bg-white rounded-sm" data-testid={`seo-heading-${slug}`} />
              <div className="text-[10px] text-[#9AA6A0] mt-0.5">Optional — e.g. “Hotel Bed Linen Suppliers”. Leave empty to keep the heading visitors see today.</div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button type="button" onClick={save} disabled={busy || !dirty} className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`seo-save-${slug}`}><Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}</button>
              {row.customised && <button type="button" onClick={reset} disabled={busy} className="text-xs text-[#B35C4A] hover:underline inline-flex items-center gap-1" data-testid={`seo-reset-${slug}`}><RotateCcw className="w-3 h-3" /> Reset to default</button>}
              <a href={row.path} target="_blank" rel="noreferrer" className="ml-auto text-[11px] text-[#264839] hover:underline" data-testid={`seo-open-${slug}`}>Open page ↗</a>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#7D8C85] mb-1.5">Google preview</div>
            <GooglePreview title={fullTitle} description={effDesc} path={row.path} testId={`seo-preview-${slug}`} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function SeoManager() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [onlyEdited, setOnlyEdited] = useState(false);
  const load = () => api.get("/admin/seo/pages").then(({ data }) => setData(data)).catch((e) => setError(e.response?.data?.detail || "Couldn't load SEO pages"));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const items = data?.items || [];
    const needle = q.trim().toLowerCase();
    return items.filter((r) => (!onlyEdited || r.customised) && (!needle || r.label.toLowerCase().includes(needle) || r.effective.title.toLowerCase().includes(needle)));
  }, [data, q, onlyEdited]);
  const edited = (data?.items || []).filter((r) => r.customised).length;

  return (
    <div className="space-y-4" data-testid="seo-manager">
      <div className="bg-white border border-[#E8ECE9]">
        <div className="p-4 border-b border-[#E8ECE9] flex flex-wrap items-center gap-3">
          <SearchIcon className="w-4 h-4 text-[#264839]" />
          <div className="flex-1 min-w-[200px]">
            <div className="font-serif text-lg text-[#11211A]">Search titles & descriptions <span className="text-sm text-[#7D8C85]" data-testid="seo-count">({data ? `${data.items.length} pages · ${edited} edited` : "…"})</span></div>
            <div className="text-xs text-[#7D8C85]">Homepage, each category and each sub-category page. Supplier listing pages build their own from the supplier's name, country and sub-categories. Visitors' page design is unchanged.</div>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a page…" className="px-3 py-1.5 text-sm border border-[#C4CFC9] rounded-sm" data-testid="seo-search" />
          <label className="inline-flex items-center gap-1.5 text-xs text-[#4A5550]"><input type="checkbox" checked={onlyEdited} onChange={(e) => setOnlyEdited(e.target.checked)} className="accent-[#264839]" data-testid="seo-only-edited" /> Edited only</label>
        </div>
        {error && <div className="p-4 text-sm text-[#B35C4A]" data-testid="seo-error">{error}</div>}
        {!error && !data && <div className="p-4 text-sm text-[#7D8C85] animate-pulse">Loading pages…</div>}
        {data && rows.length === 0 && <div className="p-4 text-sm text-[#7D8C85]">No pages match.</div>}
        {data && rows.map((r) => <SeoRow key={r.key} row={r} suffix={data.suffix} limits={data.limits} onSaved={load} />)}
      </div>
    </div>
  );
}
